'use client';

import { useState, useEffect } from 'react';

interface ScrollProgressIndicatorProps {
  showPercentage?: boolean;
  position?: 'top' | 'side';
}

const ScrollProgressIndicator = ({ 
  showPercentage = false, 
  position = 'top' 
}: ScrollProgressIndicatorProps) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const calculateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      
      setScrollProgress(Math.min(Math.max(progress, 0), 100));
      setIsVisible(scrollTop > 100);
    };

    const handleScroll = () => {
      requestAnimationFrame(calculateScrollProgress);
    };

    window.addEventListener('scroll', handleScroll);
    calculateScrollProgress();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  if (position === 'side') {
    return (
      <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:41:6" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="41" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20right-6%20top-1%2F2%20transform%20-translate-y-1%2F2%20z-100%20hidden%20lg%3Ablock%22%7D" className="fixed right-6 top-1/2 transform -translate-y-1/2 z-100 hidden lg:block">
        <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:42:8" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="42" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-1%20h-32%20bg-muted%20rounded-full%20overflow-hidden%22%7D" className="w-1 h-32 bg-muted rounded-full overflow-hidden">
          <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:43:10" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="43" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20bg-primary%20transition-all%20duration-300%20ease-out%20rounded-full%22%7D" 
            className="w-full bg-primary transition-all duration-300 ease-out rounded-full"
            style={{ height: `${scrollProgress}%` }}
          />
        </div>
        {showPercentage && (
          <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:49:10" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="49" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20text-xs%20text-text-secondary%20text-center%20font-medium%22%2C%22textContent%22%3A%22%25%22%7D" className="mt-2 text-xs text-text-secondary text-center font-medium">
            {Math.round(scrollProgress)}%
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:58:4" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="58" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20top-20%20left-0%20right-0%20z-100%22%7D" className="fixed top-20 left-0 right-0 z-100">
      <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:59:6" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="59" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-1%20bg-muted%22%7D" className="h-1 bg-muted">
        <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:60:8" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="60" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20bg-gradient-to-r%20from-primary%20to-secondary%20transition-all%20duration-300%20ease-out%22%7D" 
          className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      {showPercentage && (
        <div data-component-id="src/components/common/ScrollProgressIndicator.tsx:66:8" data-component-path="src/components/common/ScrollProgressIndicator.tsx" data-component-line="66" data-component-file="ScrollProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-2%20right-6%20text-xs%20text-text-secondary%20font-medium%20bg-white%20px-2%20py-1%20rounded%20shadow-card%22%2C%22textContent%22%3A%22%25%20complete%22%7D" className="absolute top-2 right-6 text-xs text-text-secondary font-medium bg-white px-2 py-1 rounded shadow-card">
          {Math.round(scrollProgress)}% complete
        </div>
      )}
    </div>
  );
};

export default ScrollProgressIndicator;